import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award, Search, X } from "lucide-react";
import api from "@/lib/api";
import { matchesAllTokens } from "@/lib/search";
import { SeoHead } from "@/components/public/SeoHead";
import { DetailModal } from "@/components/public/DetailModal";
import { AdSlot } from "@/components/public/AdSlot";
import { CERTIFICATION_CLAIM_TYPES, claimTypeLabel } from "@/lib/certificationTypes";

// Case-insensitive, diacritic-insensitive, punctuation-tolerant search across
// the fields that *define* each certification — name, issuing organization,
// and the short tagline. Full descriptions are excluded because they
// frequently cross-reference unrelated standards (e.g. bluesign's text
// mentions "organic" and "recycled" without bluesign being either) which
// would otherwise pollute the result list.
function certMatches(cert, query) {
  if (!query) return true;
  const haystack = [cert.name, cert.organization, cert.short_description]
    .filter(Boolean)
    .join(" ");
  return matchesAllTokens(haystack, query);
}

export default function CertificationsPage() {
  const { data = [] } = useQuery({
    queryKey: ["public-certifications"],
    queryFn: async () => (await api.get("/public/materials-certifications")).data,
  });
  const [openCert, setOpenCert] = useState(null);
  const [selected, setSelected] = useState(CERTIFICATION_CLAIM_TYPES[0].value);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => data.filter((c) => certMatches(c, query)), [data, query]);

  const grouped = useMemo(() => {
    const m = {};
    for (const c of CERTIFICATION_CLAIM_TYPES) m[c.value] = [];
    for (const c of filtered) {
      if (c.main_claim_type && m[c.main_claim_type]) m[c.main_claim_type].push(c);
    }
    return m;
  }, [filtered]);

  const trimmedQuery = query.trim();
  const isSearching = !!trimmedQuery;
  const active = CERTIFICATION_CLAIM_TYPES.find((c) => c.value === selected) || CERTIFICATION_CLAIM_TYPES[0];
  const activeCerts = grouped[active.value] || [];
  const totalMatches = filtered.length;

  // Clicking a legend tile always means "browse this claim type" — if a
  // search is active, clear it so the page drops back to the normal view.
  const selectClaimType = (val) => {
    if (query) setQuery("");
    setSelected(val);
  };

  return (
    <div data-testid="certifications-page">
      <SeoHead title="Materials Certifications — Sustainably Curated" description="Independent sustainability certifications we trust and recognize. GOTS, GRS, OEKO-TEX, FSC, and more — explained for everyday shoppers." path="/materials-certifications" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Materials Certifications</h1>
        <p className="text-muted_text">
          Independent standards that verify a brand's claims about materials and supply chains. When a clothing item carries a credible certification, we may reclassify its materials accordingly.
        </p>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-forest/70">Tap a claim type to view its certifications</p>
          <div className="relative w-full sm:w-80" data-testid="certifications-search-wrapper">
            <Search className="w-4 h-4 text-muted_text absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search certifications…"
              className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-line rounded-full focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20 placeholder:text-muted_text/80"
              data-testid="certifications-search-input"
              aria-label="Search certifications by name, organization, or description"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted_text hover:text-ink"
                data-testid="certifications-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4" role="tablist" data-testid="certifications-legend">
          {CERTIFICATION_CLAIM_TYPES.map((c) => (
            <LegendTile
              key={c.value}
              type={c}
              count={(grouped[c.value] || []).length}
              selected={!isSearching && selected === c.value}
              onSelect={() => selectClaimType(c.value)}
            />
          ))}
        </div>

      {isSearching ? (
        // Search mode — render every claim type that has matches, stacked.
        <div data-testid="certifications-search-results">
          {totalMatches === 0 ? (
            <p className="text-muted_text text-sm italic mt-4" data-testid="certifications-search-empty">
              No certifications match “{trimmedQuery}”. Try a different keyword or clear the search.
            </p>
          ) : (
            CERTIFICATION_CLAIM_TYPES.map((c) => {
              const items = grouped[c.value] || [];
              if (items.length === 0) return null;
              return (
                <section
                  key={c.value}
                  className="mb-8 scroll-mt-28"
                  data-testid={`certifications-section-${c.value}`}
                >
                  <div className="border-t border-line mt-2 mb-4" aria-hidden />
                  <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-serif text-xl">{c.label}</h2>
                    <span className="text-[11px] uppercase tracking-wider text-muted_text">
                      {items.length} {items.length === 1 ? "certification" : "certifications"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((c) => <CertCard key={c.id} cert={c} onOpen={() => setOpenCert(c)} />)}
                  </div>
                </section>
              );
            })
          )}
        </div>
      ) : (
        // Browse mode — single claim type at a time, chosen via legend tiles.
        <section className="mb-6 scroll-mt-28" data-testid={`certifications-section-${selected}`}>
          <div className="border-t border-line mt-2 mb-6" aria-hidden />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeCerts.map((c) => <CertCard key={c.id} cert={c} onOpen={() => setOpenCert(c)} />)}
            {activeCerts.length === 0 && (
              <p className="text-muted_text col-span-full text-sm italic">No certifications in this category yet.</p>
            )}
          </div>
        </section>
      )}

      {openCert && <CertificationModal cert={openCert} onClose={() => setOpenCert(null)} />}
      </section>

      <AdSlot slotKey="materials_certifications_mid" />
    </div>
  );
}

function LegendTile({ type, count, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      role="tab"
      aria-selected={selected}
      data-testid={`certifications-legend-${type.value}`}
      className={`text-left bg-white border-2 rounded-2xl px-4 py-3 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/60 ${
        selected ? "border-forest shadow-sm" : "border-line hover:border-forest/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <h3 className="font-serif text-base leading-tight flex-1 min-w-0">{type.label}</h3>
        <span className={`text-[11px] uppercase tracking-wider ${selected ? "text-forest font-medium" : "text-muted_text"}`}>
          {count}
        </span>
      </div>
    </button>
  );
}

function CertCard({ cert, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white border border-line p-4 hover:border-forest/40 transition-colors flex flex-col gap-2"
      data-testid={`cert-card-${cert.slug}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Award className="w-4 h-4 text-forest shrink-0" />
        <h2 className="font-serif text-lg truncate" title={cert.name}>{cert.name}</h2>
      </div>
      {cert.organization && <p className="text-xs text-muted_text truncate">{cert.organization}</p>}
      <p className="text-xs text-ink/85 line-clamp-3">{cert.short_description || "—"}</p>
    </button>
  );
}

function CertificationModal({ cert, onClose }) {
  const claimLabel = claimTypeLabel(cert.main_claim_type);

  const subtitle = cert.organization ? (
    <>
      <span className="uppercase tracking-wider mr-1">Issuing organization</span>
      <span className="italic">{cert.organization}</span>
    </>
  ) : null;

  const badge = claimLabel ? (
    <span className="inline-block text-[11px] uppercase tracking-wider px-2.5 py-1 bg-sand text-ink/85 border border-line whitespace-nowrap" data-testid="cert-modal-claim-type">
      {claimLabel}
    </span>
  ) : null;

  return (
    <DetailModal
      open={Boolean(cert)}
      onClose={onClose}
      testId="cert-modal"
      title={cert.name}
      subtitle={subtitle}
      badge={badge}
      externalLink={cert.external_link ? { href: cert.external_link, label: "Visit official site" } : null}
      fullPageHref={`/materials-certifications/${cert.slug}`}
    >
      {cert.short_description && (
        <p className="text-sm text-muted_text leading-relaxed">{cert.short_description}</p>
      )}
    </DetailModal>
  );
}
